import React, { useCallback, useEffect, useState } from 'react';
import { Form, Input, Switch, Row, Col, DatePicker, Flex, AutoComplete, Select, Space, Button } from 'antd';
import locales_codes from 'locale-codes';
import moment from 'moment-timezone';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useErrorHandler } from '../../../Hooks/useErrorHandler';
import { getProducts } from '../UsersServices';
import { useGlobal } from '../../Shared/GlobalContext/GlobalContext';
import { t } from 'i18next';

dayjs.extend(utc);

const locales = locales_codes?.all.map((locale) => ({
    value: locale.tag,
    label: locale.tag,
}));

const timezones = moment.tz.names().map((timezone) => ({
    value: timezone,
    label: timezone,
}));

// Helper function to find timezone by offset
const findTimezoneByOffset = (offset) => {
    // Convert numeric offset to hours
    if (typeof offset === 'number') {
        // Find timezones with matching offset
        const matchingTimezones = moment.tz.names().filter(tz => {
            return Math.abs(moment.tz(tz).utcOffset() / 60 - offset) < 0.01;
        });
        
        // Prefer common/popular timezones if multiple matches
        const commonTimezones = matchingTimezones.filter(tz => 
            tz.includes('Europe/') || 
            tz.includes('America/') || 
            tz.includes('Asia/') ||
            tz.includes('Australia/')
        );
        
        return commonTimezones.length > 0 ? commonTimezones[0] : (matchingTimezones.length > 0 ? matchingTimezones[0] : null);
    }
    return offset; // Return as is if not a number
};

const UserForm = ({ form, initialValues, screens, onFinish, onCancel }) => {
    const [submitDisabled, setSubmitDisabled] = React.useState(!initialValues);
    const [submitLoading, setSubmitLoading] = React.useState(false);
    const { setLoading } = useGlobal();
    const [products, setProducts] = useState([]);
    const { handleError } = useErrorHandler();

    useEffect(() => {
        if (initialValues) {
            // Convert numeric timezone offset to timezone string if needed
            // And convert discount_expiry string to dayjs object if present
            const transformedValues = {
                ...initialValues,
                timezone: initialValues.timezone !== undefined ? findTimezoneByOffset(initialValues.timezone) : undefined,
                discount_expiry: initialValues.discount_expiry ? dayjs(initialValues.discount_expiry) : undefined
            };
            form.setFieldsValue(transformedValues);
        } else {
            form.resetFields();
        }
    }, [initialValues, form]);

    const checkFormValues = () => {
        const values = form.getFieldsValue();
        const hasAllValues = !!values.email &&
            !!values.first_name &&
            !!values.last_name && !!(initialValues || values.password);
        setSubmitDisabled(!hasAllValues);
    };
    const handleSubmit = async (values) => {
        setSubmitLoading(true);
        try {
            await onFinish(values);
        } finally {
            setSubmitLoading(false);
        }
    };

    const getProductsCall = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await getProducts();
            setProducts(data);
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
        }
    }, [handleError, setLoading]);


    useEffect(() => {
        getProductsCall();
    }, [getProductsCall]);
    return (
        <Form
            form={form}
            layout="vertical"
            initialValues={initialValues || {}}
            className={screens.xs ? 'px-2' : 'px-4'}
            onValuesChange={checkFormValues}
            onFinish={handleSubmit}
        >
            <Row gutter={screens.xs ? 8 : 24}>
                <Col xs={24} md={12}>
                    <Form.Item
                        name="first_name"
                        label="First Name"
                        rules={[{ required: true, message: 'Please input first name!' }]}
                    >
                        <Input />
                    </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                    <Form.Item
                        name="last_name"
                        label="Last Name"
                        rules={[{ required: true, message: 'Please input last name!' }]}
                    >
                        <Input />
                    </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                    <Form.Item
                        name="email"
                        label="Email"
                        rules={[
                            { required: true, message: 'Please input email!' },
                            { type: 'email', message: 'Please input valid email!' },
                        ]}
                    >
                        <Input />
                    </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                    <Form.Item
                        name="gender"
                        label="Gender"
                        rules={[
                            { message: 'Please input valid gender!' },
                        ]}
                    >
                        <Select>
                            <Select.Option value="male">Male</Select.Option>
                            <Select.Option value="female">Female</Select.Option>
                        </Select>
                    </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                    <Form.Item
                        name="discount_percentage"
                        label="Discount Percentage"
                        rules={[
                            {
                                type: 'number',
                                min: 0,
                                max: 50,
                                message: 'Discount must be between 0% and 50%',
                                transform: value => Number(value),
                            },
                        ]}
                    >
                        <Input type="number" min={0} max={50} />
                    </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                    <Form.Item
                        label="Discount Expiry"
                        name="discount_expiry"
                    >
                        <DatePicker 
                            style={{ width: '100%' }} 
                            format="YYYY-MM-DD"
                            showTime={false}
                            utc
                        />
                    </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                    <Form.Item
                        label="Locale"
                        name="locale"
                        style={{ width: '100%' }}
                    >
                        <AutoComplete
                            options={locales}
                            placeholder="Select Locale"
                            size="medium"
                            filterOption={(inputValue, option) =>
                                option.label.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                            }
                        />
                    </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                    <Form.Item
                        label="Timezone"
                        name="timezone"
                        style={{ width: '100%' }}
                    >
                        <AutoComplete
                            options={timezones}
                            placeholder="Select Timezone"
                            size="medium"
                            filterOption={(inputValue, option) =>
                                option.label.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                            }
                        />
                    </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                    <Form.Item
                        name="password"
                        label="Password"
                        rules={[
                            { required: !initialValues, message: 'Please input password!' },
                            { min: 8, message: 'Password must be at least 8 characters!' },
                        ]}
                    >
                        <Input.Password />
                    </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                    <Form.Item
                        name="loyalty_level"
                        label={t('select_plan')}
                    >
                        <Select>
                            {products.map(product => <Select.Option key={product.id} value={product.loyalty_level}>{`${product.title} (${product.coins} coins)`}</Select.Option>)}
                        </Select>
                    </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                    <Flex gap={4}>
                        <Form.Item style={{ flexGrow: 1 }} name="is_disabled" label="Disabled Account" valuePropName="checked">
                            <Switch />
                        </Form.Item>
                        <Form.Item style={{ flexGrow: 1 }} name="is_admin" label="Admin Access" valuePropName="checked">
                            <Switch />
                        </Form.Item>
                        <Form.Item style={{ flexGrow: 1 }} name="is_verified" label="Verified Account" valuePropName="checked">
                            <Switch />
                        </Form.Item>
                    </Flex>
                </Col>
            </Row>
            <Form.Item style={{ marginBottom: 4, marginTop: 16 }}>
                <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                    <Button onClick={onCancel}>Cancel</Button>
                    <Button
                        type="primary"
                        htmlType="submit"
                        disabled={submitDisabled}
                        loading={submitLoading}
                    >
                        {initialValues ? 'Update User' : 'Create User'}
                    </Button>
                </Space>
            </Form.Item>
        </Form>
    );
};

export default UserForm;
